export function PricingStrategies() {
  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Pricing Strategies for Maximum Returns
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Setting the right price is one of the most critical tips for selling an apartment building successfully. 
            Price too high and you'll scare away qualified buyers; price too low and you'll leave money on the table. 
            Learn how to determine the optimal asking price that attracts serious investors while maximizing your returns.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/d3b367a9396ec2099d3dc900e8c78942.jpg"
              alt="Apartment building pricing analysis"
              className="w-full h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Understanding Valuation Methods</h3>
            <p className="text-gray-600 mb-6">
              Commercial real estate, including apartment buildings, is valued differently than residential properties. 
              Understanding these valuation methods is essential for setting a competitive and realistic asking price. 
              Most buyers will use one or more of these approaches to determine their offer price.
            </p>
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h4 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-line-chart-line text-orange-500 mr-2"></i>
                  Income Capitalization Approach
                </h4>
                <p className="text-gray-600 mb-3">
                  The most common method for valuing apartment buildings. This approach divides the property's 
                  Net Operating Income (NOI) by the market capitalization rate (cap rate) to determine value.
                </p>
                <div className="bg-orange-50 p-4 rounded-lg">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Formula: Property Value = NOI ÷ Cap Rate</p>
                  <p className="text-sm text-gray-600">
                    Example: $200,000 NOI ÷ 6% cap rate = $3,333,333 property value
                  </p>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h4 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-home-line text-green-500 mr-2"></i>
                  Sales Comparison Approach
                </h4>
                <p className="text-gray-600 mb-3">
                  Compares your property to similar apartment buildings that have recently sold in your market. 
                  Adjustments are made for differences in size, condition, location, and amenities.
                </p>
                <p className="text-sm text-gray-600">
                  Most useful when there are recent comparable sales of similar properties in your area.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h4 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-calculator-line text-red-500 mr-2"></i>
                  Cost Approach
                </h4>
                <p className="text-gray-600">
                  Calculates what it would cost to rebuild the property from scratch, minus depreciation, plus 
                  land value. Less commonly used for apartment buildings but helpful for newer properties or 
                  unique situations.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-12 mb-16">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Key Factors That Impact Your Apartment Building's Value
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-money-dollar-circle-line text-2xl text-green-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Net Operating Income</h4>
              <p className="text-gray-600 text-sm">
                Your NOI is the single most important factor in determining value. Higher rental income and 
                lower operating expenses directly increase your property's worth. Focus on maximizing NOI 
                before listing by increasing rents to market rates and reducing unnecessary expenses.
              </p>
            </div>
            <div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-map-pin-line text-2xl text-orange-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Location and Market</h4>
              <p className="text-gray-600 text-sm">
                Properties in strong rental markets with low vacancy rates, job growth, and population 
                increases command premium prices. Location factors include neighborhood quality, school 
                districts, proximity to employment centers, and local amenities.
              </p>
            </div>
            <div>
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-building-4-line text-2xl text-red-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Property Condition</h4>
              <p className="text-gray-600 text-sm">
                Well-maintained properties with updated systems, modern amenities, and minimal deferred 
                maintenance sell for higher prices. Buyers pay premiums for turnkey properties that won't 
                require immediate capital expenditures.
              </p>
            </div>
            <div>
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-percent-line text-2xl text-indigo-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Occupancy Rate</h4>
              <p className="text-gray-600 text-sm">
                Higher occupancy rates indicate strong demand and stable income, making your property more 
                attractive to buyers. Properties with 90%+ occupancy typically sell faster and at higher 
                prices than those with significant vacancy.
              </p>
            </div>
            <div>
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-file-text-line text-2xl text-teal-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Lease Terms</h4>
              <p className="text-gray-600 text-sm">
                Long-term leases with quality tenants provide income stability that buyers value. Properties 
                with staggered lease expirations reduce turnover risk and are more attractive than those 
                with many leases expiring simultaneously.
              </p>
            </div>
            <div>
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-arrow-up-line text-2xl text-amber-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Value-Add Potential</h4>
              <p className="text-gray-600 text-sm">
                Properties with opportunities for rent increases, unit upgrades, or operational improvements 
                attract investors looking for upside potential. Clearly document these opportunities to 
                justify your asking price.
              </p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Strategic Pricing Tactics</h3>
            <p className="text-gray-600 mb-6">
              Beyond understanding valuation methods, implementing smart pricing tactics is among the most 
              effective tips for selling an apartment building quickly and profitably. Your pricing strategy 
              should consider market conditions, buyer psychology, and your timeline for selling.
            </p>
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-500">
                <h4 className="font-semibold text-gray-900 mb-2">Price Slightly Below Market</h4>
                <p className="text-gray-600 text-sm">
                  Pricing 3-5% below comparable properties can generate multiple offers and create competition 
                  among buyers, potentially driving the final price above asking. This strategy works best in 
                  strong markets with high buyer demand.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-orange-500">
                <h4 className="font-semibold text-gray-900 mb-2">Use Psychological Pricing</h4>
                <p className="text-gray-600 text-sm">
                  Price at $2,995,000 instead of $3,000,000. While the difference is minimal, properties 
                  priced just below round numbers appear in more search results and feel more negotiable 
                  to buyers, generating more interest and showings.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-red-500">
                <h4 className="font-semibold text-gray-900 mb-2">Build in Negotiation Room</h4>
                <p className="text-gray-600 text-sm">
                  Most buyers expect to negotiate. Price 5-10% above your minimum acceptable price to allow 
                  room for negotiation while still achieving your target. This makes buyers feel they're 
                  getting a deal while you reach your goals.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-indigo-500">
                <h4 className="font-semibold text-gray-900 mb-2">Consider Market Timing</h4>
                <p className="text-gray-600 text-sm">
                  Spring and fall typically see the most buyer activity for commercial properties. If you're 
                  not in a rush, timing your listing for these peak seasons can result in more offers and 
                  better prices than listing during slower periods.
                </p>
              </div>
            </div>
          </div>
          <div>
            <img 
              src="https://readdy.ai/api/search-image?query=real%20estate%20pricing%20strategy%20meeting%20with%20investors%20reviewing%20property%20valuation%20documents%20financial%20charts%20market%20analysis%20professional%20business%20discussion%20negotiation%20planning%20commercial%20real%20estate%20deal%20modern%20conference%20room&width=800&height=600&seq=tips-pricing-002&orientation=landscape"
              alt="Pricing strategy discussion"
              className="w-full h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
        </div>
      </div>
    </section>
  );
}